
import { defineComponent } from "vue";
import { TableEditingConfig } from "@/components/controls/catalogueGrid/tableEditingConfig";
import { TableSelectionConfig } from "@/components/controls/catalogueGrid/tableSelectionConfig";
import { TableDesignConfig } from "@/components/controls/catalogueGrid/tableDesignConfig";
import { TextColumnConfig } from "@/components/controls/catalogueGrid/columns/textColumnConfig";
import { IntegerColumnConfig } from "@/components/controls/catalogueGrid/columns/integerColumnConfig";
import { requiredRule } from "@/components/controls/catalogueGrid/columns/baseValidationRules";
import { packageLengthRule } from "@/components/controls/catalogueGrid/columns/packageLengthRule";
import CatalogueGrid from "@/components/controls/catalogueGrid/CatalogueGrid.vue";
import { BooleanColumnConfig } from "@/components/controls/catalogueGrid/columns/booleanColumnConfig";
import { DecimalColumnConfig } from "@/components/controls/catalogueGrid/columns/decimalColumnConfig";
import { DateColumnConfig } from "@/components/controls/catalogueGrid/columns/dateColumnConfig";
import { TableImportConfig } from "@/components/controls/catalogueGrid/tableImportConfig";
import { TableExportConfig } from "@/components/controls/catalogueGrid/tableExportConfig";
import { CatalogueColumnConfig } from "@/components/controls/catalogueGrid/columns/catalogueColumnConfig";
import PackagesSelectionWorkflow from "@/components/workflows/packagesSelection/packagesSelectionWorkflow.vue";
import FilterDialog from "./FilterDialog.vue";
import api from "@/services/api";
import store from "@/store";

export default defineComponent({
  components: {
    CatalogueGrid,
    PackagesSelectionWorkflow,
    FilterDialog,
  },
  props: {
    //показатель режима выбора
    selectMode: { required: false, type: Boolean, default: false },
    gridHeight: { required: false, type: String, default: null },
    //функция для получения выбранных элементов
    getSelectedData: { type: Function },
    //функция для вызова при изменении выбранных элементов
    changeSelectedData: { type: Function },
    //модель родительского элемента
    parentModel: { required: true },
    //идет расчет плана
    isCalculation: { required: false },
    //открыты ли настройки
    isSettingsOpen: { type: Object, required: true },
  },
  methods: {
    async applyFilter(filter: any) {
      let dxGrid = this.$refs.grid.dxGrid;
      this.$refs.grid.updateAdditionalFilters(null);
      dxGrid.clearFilter("row");
      dxGrid.clearFilter("header");
      dxGrid.filter("FilterValues", "=", JSON.stringify(filter));
      const temp = {
        filter: JSON.stringify(filter),
        index: this.parentModel.id,
      };
      store.dispatch("packageFilter/setPackageFilter", temp);
      this.isFilter = true;
    },
    resetFilter() {
      this.$refs.grid.updateAdditionalFilters(null);
      this.$refs.grid.dxGrid.clearFilter("dataSource");
      const temp = {
        filter: null,
        index: this.parentModel.id,
      };
      store.dispatch("packageFilter/setPackageFilter", temp);
      this.isFilter = false;
    },
    showFilterDialog() {
      this.$refs.filterDialog.show(true);
    },
    getScrollSettings() {
      const packageSettings = (store as any).state.packageSettings
        .packageSettings[this.parentModel.id];
      if (packageSettings) {
        this.designConfig.horizontalScroll = packageSettings.scroll;
      }
    },
    rememberScrollSettings(scroll: any) {
      const temp = {
        settings: {
          scroll: scroll,
        },
        index: this.parentModel.id,
      };
      store.dispatch("packageSettings/setPackageSettings", temp);
    },
    onRowValidating(e) {
      if (e.newData.minLength || e.newData.maxLength) {
        let min = e.newData.minLength
          ? e.newData.minLength
          : e.oldData.minLength;
        let max = e.newData.maxLength
          ? e.newData.maxLength
          : e.oldData.maxLength;
        if (min > max) {
          e.errorText =
            "Значение максимальной длины не может быть меньше минимальной";
          e.isValid = false;
        }
      }
    },
    onUpdateParentModelCallback() {
      const grid = this.$refs.grid.dxGrid;
      this.updateSelectionButton(grid);
    },
    onStartPackagesSelection() {
      (this.$refs.packagesSelectionWorkflow as any).start(this.parentModel?.id);
    },
    onDataFillApply() {
      const grid = this.$refs.grid.dxGrid;
      this.updateSelectionButton(grid);
    },
    onImportClose() {
      const grid = this.$refs.grid.dxGrid;
      this.updateSelectionButton(grid);
    },
    onSelectionClose() {
      const grid = this.$refs.grid.dxGrid;
      this.updateSelectionButton(grid);
      grid.columnOption("isSelectedForPlanning", "filterValue", true);
      grid.refresh();
    },
    async onToolbarPreparing(e: any) {
      if (this.repaintPreventKey) return;
      this.repaintPreventKey = true;
      this.packageSelectionButton = e.toolbarOptions.items.filter(
        (x: any) =>
          x.options && x.options.name === this.packageSelectionButtonName
      )[0];

      this.updateSelectionButton(e.component);
    },
    onRowInserted(e: any) {
      this.updateSelectionButton(e.component);
    },
    onRowUpdated(e: any) {
      this.updateSelectionButton(e.component);
    },
    onRowsRemoved(e: any) {
      this.updateSelectionButton(e.component);
    },
    async updateSelectionButton(grid: any) {
      if (this.isCalculation) return;
      const { data } = await api.get("Plan/IsStartPackageSelectionActive", {
        params: {
          planId: this.parentModel.id,
        },
      });
      this.repaintPreventKey = true;
      this.packageSelectionButton.options.disabled = !data;
      if (this.isCalculation === true) {
        this.packageSelectionButton.options.disabled = true;
      }
      grid.repaint();
    },
    changeCompact() {
      this.$emit("change-compact", true);
    },
  },
  data() {
    return {
      additionalFilters: null,
      isFilter: false,
      // подробности работы custom summary см. на сервере в методе SetCustomSummary
      summaryConfig: {
        totalItems: [
          {
            name: "TotalSummary",
            showInColumn: "number",
            displayFormat: "Итог:",
            summaryType: "custom",
          },
          {
            name: "WeightSummary",
            showInColumn: "weight",
            column: "weight",
            valueFormat: "#,##0.#",
            displayFormat: "{0} т.",
            summaryType: "sum",
          },

          {
            name: "TotalWeightSummary",
            showInColumn: "totalWeight",
            column: "totalWeight",
            valueFormat: "#,##0.#",
            displayFormat: "{0} т",
            summaryType: "sum",
          },

          {
            name: "VolumeSummary",
            showInColumn: "volume",
            column: "volume",
            valueFormat: "#,##0.#",
            displayFormat: "{0} м³",
            summaryType: "sum",
          },

          {
            name: "TotalVolumeSummary",
            showInColumn: "totalVolume",
            column: "totalVolume",
            valueFormat: "#,##0.#",
            displayFormat: "{0} м³",
            summaryType: "sum",
          },

          {
            name: "CountSummary",
            showInColumn: "count",
            column: "count",
            valueFormat: "#,##0.#",
            displayFormat: "{0} шт.",
            summaryType: "sum",
          },
        ],
        groupItems: [
          {
            name: "GroupSummary",
            showInColumn: "number",
            displayFormat: "Итог:",
            summaryType: "custom",
            showInGroupFooter: "true",
          },

          {
            name: "WeightGroupSummary",
            showInColumn: "weight",
            column: "weight",
            valueFormat: "#,##0.#",
            displayFormat: "{0} т.",
            summaryType: "sum",
            showInGroupFooter: "true",
          },

          {
            name: "TotalWeightGroupSummary",
            showInColumn: "totalWeight",
            column: "totalWeight",
            valueFormat: "#,##0.#",
            displayFormat: "{0} т",
            summaryType: "sum",
            showInGroupFooter: "true",
          },

          {
            name: "VolumeGroupSummary",
            showInColumn: "volume",
            column: "volume",
            valueFormat: "#,##0.#",
            displayFormat: "{0} м³",
            summaryType: "sum",
            showInGroupFooter: "true",
          },

          {
            name: "TotalVolumeGroupSummary",
            showInColumn: "totalVolume",
            column: "totalVolume",
            valueFormat: "#,##0.#",
            displayFormat: "{0} м³",
            summaryType: "sum",
            showInGroupFooter: "true",
          },

          {
            name: "CountGroupSummary",
            showInColumn: "count",
            column: "count",
            valueFormat: "#,##0.#",
            displayFormat: "{0} шт.",
            summaryType: "sum",
            showInGroupFooter: "true",
          },
        ],
      },

      editingConfig: new TableEditingConfig({
        allowDeleteAll: !this.isCalculation,
        allowUpdating: !this.isCalculation,
        allowAdding: !this.isCalculation,
        allowDeleting: !this.isCalculation,
        confirmDelete: true,
        allowDataFill: !this.isCalculation,
        allowExport: true,
        allowImport: !this.isCalculation,
        allowClearFilter: true,
        allowCopy: true,
        allowDeleteMultipleRows: true,
        allowChangeScrollSettings: true,
        allowColumnReordering: true,
        allowGrouping: true,
        allowResetSelectedRows: true,
        enableMasterDetail: true,
        detailComponentName:
          "components/journals/PlanPackageJournal/PlanPackageDetail.vue",
        mode: "row",
        chooseColumns: true,
      }),
      selectModeEditingConfig: new TableEditingConfig({
        allowUpdating: false,
        allowAdding: false,
        allowDeleting: false,
        confirmDelete: false,
        allowDataFill: false,
        allowExport: false,
        allowDeleteMultipleRows: false,
      }),
      designConfig: new TableDesignConfig({
        popUpColumnsCount: 1,
        popUpWidth: "850px",
        heightScroll: "calc(100vh - 350px)",
        horizontalScroll: true,
      }),
      columns: [
        new TextColumnConfig({
          dataField: "number",
          caption: "Номер",
        }),

        new IntegerColumnConfig({
          dataField: "minLength",
          caption: "Мин. длина (мм)",
          min: 0,
          validationRules: [packageLengthRule],
        }),

        new IntegerColumnConfig({
          dataField: "maxLength",
          caption: "Макс. длина (мм)",
          min: 0,
          validationRules: [packageLengthRule],
        }),

        new IntegerColumnConfig({
          dataField: "width",
          caption: "Ширина пакета (мм)",
          min: 0,
        }),

        new IntegerColumnConfig({
          dataField: "height",
          caption: "Высота пакета (мм)",
          min: 0,
        }),

        new IntegerColumnConfig({
          dataField: "boardHeight",
          caption: "Толщина доски (мм)",
          min: 0,
          validationRules: [requiredRule],
        }),

        new IntegerColumnConfig({
          dataField: "boardWidth",
          caption: "Ширина доски (мм)",
          min: 0,
          validationRules: [requiredRule],
        }),

        new DecimalColumnConfig({
          dataField: "weight",
          caption: "Вес (кг)",
          decimalPoints: 0,
          hideFraction: true,
          validationRules: [requiredRule],
        }),

        new DecimalColumnConfig({
          dataField: "totalWeight",
          caption: "Общий вес (кг)",
          decimalPoints: 1,
          hideFraction: true,
          readOnly: true,
        }),

        new DecimalColumnConfig({
          dataField: "volume",
          caption: "Объем (м³)",
          decimalPoints: 0,
          validationRules: [requiredRule],
        }),

        new DecimalColumnConfig({
          dataField: "totalVolume",
          caption: "Общий объем (м³)",
          decimalPoints: 1,
          readOnly: true,
        }),

        new DateColumnConfig({
          dataField: "formationDate",
          caption: "Дата формирования",
          mode: "datetime",
          validationRules: [requiredRule],
        }),

        new TextColumnConfig({
          dataField: "storagePlace",
          caption: "Складское место",
        }),

        new CatalogueColumnConfig({
          dataField: "packageVolumeTypeDescription",
          caption: "Тип пакета по объему",
          modelIdField: "packageVolumeTypeId",
          modelDescField: "packageVolumeTypeDescription",
          keyField: "id",
          displayField: "packageVolumeTypeDescription",
          displayDescField: "name",
          catalogueComponentName:
            "catalogues/PackageVolumeTypeCatalogue/PackageVolumeTypeCatalogue",
          descriptionColumnName: "packageVolumeTypeDescription",
          allowEmpty: true,
        }),

        new IntegerColumnConfig({
          dataField: "count",
          caption: "Кол-во (шт)",
          min: 0,
          validationRules: [requiredRule],
        }),

        new TextColumnConfig({
          dataField: "appropriateOrderNomenclatures",
          caption: "Позиции заказа",
          readOnly: true,
        }),

        new BooleanColumnConfig({
          dataField: "isSelectedForPlanning",
          caption: "Выбран для планирования",
        }),
      ],
      importConfig: new TableImportConfig({
        progressMethodName: "importPlanPackageProgress",
        progressEventName: "importPlanPackageProgressEvent",
        messageEventName: "importPlanPackageMessageEvent",
      }),
      exportConfig: new TableExportConfig({
        progressMethodName: "exportPlanPackageProgress",
        progressEventName: "exportPlanPackageProgressEvent",
        messageEventName: "exportPlanPackageMessageEvent",
        exportFileName: "Пакеты-Opti-Loading",
      }),
      selectionConfig: new TableSelectionConfig({
        mode: "multiple",
        selectAllMode: "allPages",
        showCheckBoxesMode: "none",
      }),
      pageSizes: [10, 20, 50, 100],
      packageSelectionButton: {},
      packageSelectionButtonName: "packageSelectionButton",
      repaintPreventKey: false,
      toolbarItems: this.isCalculation
        ? []
        : [
            {
              location: "before",
              widget: "dxButton",
              options: {
                text: "Пакеты компактно",
                stylingMode: "outlined",
                onClick: this.changeCompact,
              },
            },
            {
              location: "before",
              widget: "dxButton",
              options: {
                name: "packageSelectionButton",
                icon: "check",
                hint: "Подбор пакетов по заказам",
                stylingMode: "text",
                onClick: this.onStartPackagesSelection,
                disabled: true,
              },
            },
            {
              location: "before",
              widget: "dxButton",
              options: {
                name: "propertyFilterButton",
                icon: "filter",
                hint: "Фильтр по свойствам пакета",
                stylingMode: "text",
                onClick: this.showFilterDialog,
              },
            },
          ],
    };
  },
  created() {
    this.getScrollSettings();
    if (this.selectMode) {
      this.editingConfig = this.selectModeEditingConfig;
    }
    if (this.gridHeight) {
      this.designConfig.height = this.gridHeight;
    }
    let filter = (store as any).state.packageFilter.packageFilter[
      this.parentModel.id
    ];
    if (filter) {
      this.additionalFilters = [];
      this.additionalFilters.push("FilterValues", "=", filter);
      this.isFilter = true;
    }
  },
});
